'use client';

import {
  cloneElement,
  ComponentPropsWithRef,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useClickOutside } from '@wishket/yogokit';

import { SelectionPopoverContent } from './parts';
import { usePopoverPosition, useSelectionData } from './hooks';
import { checkSelectionActive } from './utils';

import { BubblePopover } from '@/shared/Components';
import { useDebounce, useDeviceCheck, useHighlight } from '@/shared/hooks';

const DEFAULT_VALID_TAGS = ['p', 'li', 'h2', 'h3', 'h4'] as const;
type DefaultValidTag = (typeof DEFAULT_VALID_TAGS)[number];

const HighlightableTag = ({ children }: PropsWithChildren) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const tagRef = useRef(null);
  const popoverRef = useRef(null);
  const { selectionInfo, getSelectionInfo, getTagIndex } =
    useSelectionData('article');
  const { popoverPosition, updatePopoverPosition } = usePopoverPosition();
  const { isDesktop } = useDeviceCheck();
  const { content } = useHighlight();

  const popoverInit = () => {
    setIsPopoverOpen(false);
    getSelectionInfo(content);
  };

  const openPopover = () => setIsPopoverOpen(true);

  const closePopover = () => {
    const hasSelection = checkSelectionActive();
    if (hasSelection) return;
    popoverInit();
  };

  const handleSelection = useDebounce(() => {
    if (!tagRef.current) return;
    const element = tagRef.current as HTMLDivElement;
    const currentTag = element.childNodes[0];
    const currentTagIndex = getTagIndex(currentTag);

    const hasSelection = checkSelectionActive();
    if (!hasSelection) return popoverInit();

    const currentSelectionInfo = getSelectionInfo(content);

    if (!currentSelectionInfo || !currentSelectionInfo.text) {
      return popoverInit();
    }

    const isTargetTag = currentSelectionInfo.startTagIndex === currentTagIndex;
    if (!isTargetTag) return;

    openPopover();
    updatePopoverPosition();
  }, 200);

  useClickOutside(popoverRef, closePopover);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelection);
    return () => {
      document.removeEventListener('selectionchange', handleSelection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isValidElement(children)
        ? cloneElement(
            children as ReactElement<ComponentPropsWithRef<DefaultValidTag>>,
            {
              ref: tagRef,
              className:
                `${(children as ReactElement<ComponentPropsWithRef<DefaultValidTag>>).props.className || ''} relative`.trim(),
              children: (
                <>
                  {
                    (
                      children as ReactElement<
                        ComponentPropsWithRef<DefaultValidTag>
                      >
                    ).props.children
                  }
                  <BubblePopover
                    isHighlight
                    variant={isDesktop ? 'up' : 'down'}
                    ref={popoverRef}
                    open={isPopoverOpen}
                    content={
                      <SelectionPopoverContent
                        selectionInfo={selectionInfo}
                        popoverInit={popoverInit}
                      />
                    }
                    className="absolute"
                    style={popoverPosition}
                  />
                </>
              ),
            }
          )
        : children}
    </>
  );
};

export default HighlightableTag;
